(function() {
  // User Configuration
  const USER_CONFIG = {
    enabled: true,
    showSoldOutFitTypes: true,
    debugMode: false,
    styles: {
      marginTop: '20px',
      marginBottom: '20px',
      buttonGap: '5px',
      buttonPadding: '0'
    }
  };

  // System Configuration
  const SYSTEM_CONFIG = {
    metafieldNamespace: 'custom',
    metafieldKey: 'fit_type',
    productMetafieldsId: 'product-variant-metafields',
    sizeOptionSelector: '#swatch-option1', // Selector for the Size swatch container
    variantSwatchKingVarName: 'VariantSwatchKing',
    fitTypePropertyName: 'Fit Type',
    contextSelectors: [
      { name: 'Main Product Page', selector: '.section-product-template', metafieldSelector: '#product-variant-metafields' },
      { name: 'Quick View Modal', selector: '#quick-buy-modal', metafieldSelector: '#product-variant-metafields', triggerEvents: ['class', 'style'] }
    ],
    swatchContainerSelector: '.swatches-type-products, .product-detail__form__options'
  };

  // --- Internal State & Setup ---
  const initializedContainers = new Set();
  let currentMainProductFitType = null;
  let currentMainProductSize = null;
  const contextRegistry = [...SYSTEM_CONFIG.contextSelectors];
  
  // Cache for commonly accessed elements to improve performance
  const elementCache = {};
  
  // Queue for add-on cart operations to prevent race conditions
  const addonQueue = {
    queue: [],
    isProcessing: false,
    
    add: function(task) {
      this.queue.push(task);
      if (!this.isProcessing) {
        this.processNext();
      }
    },
    
    processNext: function() {
      if (this.queue.length === 0) {
        this.isProcessing = false;
        return;
      }
      
      this.isProcessing = true;
      const task = this.queue.shift();
      
      task()
        .then(() => {
          setTimeout(() => this.processNext(), 250); // Add slight delay between operations
        })
        .catch(() => {
          setTimeout(() => this.processNext(), 250); // Continue queue even on error
        });
    }
  };

  // Helper for conditional logging
  const log = (...args) => USER_CONFIG.debugMode && console.log(...args);
  const warn = (...args) => USER_CONFIG.debugMode && console.warn(...args);
  const error = (...args) => USER_CONFIG.debugMode && console.error(...args);

  // Cached DOM getter
  function getFromCache(key, selector, context = document) {
    if (!elementCache[key]) {
      elementCache[key] = context.querySelector(selector);
    }
    return elementCache[key];
  }
  
  // Clear cache entries related to a specific context
  function clearCacheForContext(contextElement) {
    for (const key in elementCache) {
      if (elementCache[key] && contextElement.contains(elementCache[key])) {
        delete elementCache[key];
      }
    }
  }

  window.FitTypeSelector = window.FitTypeSelector || {};
  window.FitTypeSelector.registerContext = function(contextConfig) {
    contextRegistry.push(contextConfig);
    log(`Registered new context: ${contextConfig.name}`);
    document.querySelectorAll(contextConfig.selector).forEach(element => {
      initializeContext(element, contextConfig);
    });
    return true;
  };

  if (!USER_CONFIG.enabled) {
    log("Fit Type Selector disabled by USER_CONFIG.");
    return;
  }

  // --- Core Functions ---

  // Modify Add-on Buttons on Load/Update
  function modifyAddonButtonAttributes() {
      const addonSubmitButtons = document.querySelectorAll('.additional-add_ons .add-ons-btn button[name="add"][type="submit"]');
      log(`Found ${addonSubmitButtons.length} add-on submit buttons to modify.`);
      addonSubmitButtons.forEach(button => {
          const idContainer = button.parentElement.querySelector('div[data-name="add-on-id"][data-value]');
          const variantId = idContainer ? idContainer.dataset.value : null;
          if (variantId) {
              button.type = 'button';
              button.dataset.variantId = variantId;
              button.classList.add('add-on-ajax-button--js-modified'); // Class for easier targeting
              log(`Modified add-on button for variant ${variantId}: Set type="button", added data-variant-id.`);
          } else {
              warn("Could not find variant ID for an add-on button. Button type not changed.", button);
          }
      });
  }

  // Main initialization
  function initialize() {
    try {
      log("Initializing Fit Type Selector...");
      modifyAddonButtonAttributes(); // Modify buttons first
      contextRegistry.forEach(contextConfig => {
        document.querySelectorAll(contextConfig.selector).forEach(element => {
          initializeContext(element, contextConfig);
        });
      });
      setupGlobalMutationObserver();
      setupAddonAjaxListener(); // Setup listener for modified buttons
      log("Fit Type Selector initialization complete.");
    } catch (err) {
      error("Error initializing fit type selector:", err);
    }
  }

  // Initialize a specific context (product page, quick view)
  function initializeContext(contextElement, contextConfig) {
     if (!contextElement || contextElement.dataset.fitTypeInitialized === 'true') return;
     log(`Checking context: ${contextConfig.name}`, contextElement);
     
     // Clear any cached elements related to this context to prevent stale references
     clearCacheForContext(contextElement);
     
     const isPotentiallyVisible = contextElement.classList.contains('active') || 
                                 contextElement.classList.contains('open') || 
                                 contextElement.classList.contains('reveal') || 
                                 contextElement.style.display !== 'none' || 
                                 contextElement.offsetParent !== null;
                                 
     if (contextConfig.triggerEvents && !isPotentiallyVisible) {
         log(`Context ${contextConfig.name} is not active, setting up observer.`);
         setupContextObserver(contextElement, contextConfig);
     } else {
         processContext(contextElement, contextConfig);
         contextElement.dataset.fitTypeInitialized = 'true';
     }
  }

  // Process a context to find metafields and init swatches
  function processContext(contextElement, contextConfig) {
    log(`Processing context: ${contextConfig.name}`, contextElement);
    const metafieldsSelector = contextConfig.metafieldSelector || `#${SYSTEM_CONFIG.productMetafieldsId}`;
    let metafieldsElement = contextElement.querySelector(metafieldsSelector);
    if (!metafieldsElement) {
        log(`No metafields element found using selector "${metafieldsSelector}" in context: ${contextConfig.name}`);
        const globalMetafields = document.getElementById(SYSTEM_CONFIG.productMetafieldsId);
        if(globalMetafields) {
             log("Using globally found metafields as fallback.");
             metafieldsElement = globalMetafields;
        } else {
            log(`No metafields found globally either.`);
            return;
        }
    }
    processMetafields(metafieldsElement, contextElement);
  }

  // Parse metafields and initialize swatch containers
   function processMetafields(metafieldsElement, contextElement) {
       try {
          const productData = JSON.parse(metafieldsElement.textContent);
          if (!productData || !productData.variants || !productData.variants.some(v => v.hasOwnProperty('fitType') && v.fitType !== null)) {
            log(`No valid 'fitType' metafields found in product data for context:`, contextElement);
            return;
          }
          initializeSwatchContainers(contextElement, productData);
        } catch (err) {
          error(`Error parsing metafields or processing context:`, err, contextElement);
        }
  }

  // Find and initialize variant option containers
  function initializeSwatchContainers(contextElement, productData) {
    const swatchContainers = contextElement.querySelectorAll(SYSTEM_CONFIG.swatchContainerSelector);
    if (swatchContainers.length === 0) {
      log(`No swatch containers found using selector "${SYSTEM_CONFIG.swatchContainerSelector}" in context`, contextElement);
      return;
    }
    swatchContainers.forEach(container => {
      // Skip if already initialized or if inside an add-on section
      if (initializedContainers.has(container) || container.closest('.additional-add_ons')) {
           if(container.closest('.additional-add_ons') && !container.dataset.addonSkipped) {
               log("Skipping fit type initialization within an add-on container:", container);
               container.dataset.addonSkipped = 'true';
           }
           return;
      }
      const sizeSection = container.querySelector(SYSTEM_CONFIG.sizeOptionSelector);
      if (sizeSection) {
        if (!container.querySelector('.swatch-fit-type-container')) {
             createFitTypeButtons(container, sizeSection, productData);
             initializedContainers.add(container);
             log("Initialized fit type buttons for container", container);
        } else if (!container.dataset.fitTypeExistsLogged) {
             log("Fit type buttons already exist for container", container);
             container.dataset.fitTypeExistsLogged = 'true';
        }
      } else if (!container.dataset.sizeNotFoundLogged) {
        log(`Size section ("${SYSTEM_CONFIG.sizeOptionSelector}") not found within swatch container`, container);
        container.dataset.sizeNotFoundLogged = 'true';
      }
    });
  }

  // AJAX Listener for dynamically modified add-on buttons
  function setupAddonAjaxListener() {
    document.body.addEventListener('click', function(event) {
        const addonButton = event.target.closest('.additional-add_ons .add-ons-btn button[type="button"].add-on-ajax-button--js-modified');
        if (addonButton) {
            event.preventDefault();
            const variantId = addonButton.dataset.variantId;
            const quantity = 1;
            if (!variantId) { error('Add-on Variant ID not found on button dataset.'); return; }
            
            const originalButtonText = addonButton.innerHTML;
            addonButton.disabled = true;
            addonButton.innerHTML = 'Adding...';
            
            // Add to queue instead of immediate execution
            addonQueue.add(() => {
                const formData = { 'items': [{ 'id': variantId, 'quantity': quantity }] };
                return fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify(formData)
                })
                .then(response => {
                    if (!response.ok) { 
                        return response.json().then(errorData => { 
                            throw new Error(errorData.description || errorData.message || 'Failed to add item'); 
                        }); 
                    }
                    return response.json();
                })
                .then(data => {
                    log('Add-on successfully added via AJAX:', data);
                    addonButton.innerHTML = 'Added!';
                    
                    // Trigger cart update event
                    document.dispatchEvent(new CustomEvent('cart:item-added', { 
                        bubbles: true, 
                        detail: { item: data }
                    }));
                    
                    // Update cart count (adjust selector for your theme)
                    updateCartCountUI();
                    
                    setTimeout(() => { 
                        addonButton.disabled = false; 
                        addonButton.innerHTML = originalButtonText; 
                    }, 2500);
                    
                    return Promise.resolve();
                })
                .catch((err) => {
                    error('Error adding add-on via AJAX:', err);
                    addonButton.innerHTML = 'Error';
                    setTimeout(() => { 
                        addonButton.disabled = false; 
                        addonButton.innerHTML = originalButtonText; 
                    }, 3000);
                    
                    return Promise.reject(err);
                });
            });
        }
    });
    log("Add-on AJAX listener initialized (expects modified buttons).");
  }
  
  // Update cart count UI - extracted for reuse
  function updateCartCountUI() {
    const cartCountBubble = document.querySelector('.cart-count-bubble span');
    if (cartCountBubble) {
        fetch('/cart.js')
            .then(res => res.json())
            .then(cart => {
                cartCountBubble.textContent = cart.item_count;
                const bubble = cartCountBubble.closest('.cart-count-bubble');
                if (bubble) {
                    bubble.classList.toggle('hidden', cart.item_count === 0);
                }
            })
            .catch(e => error("Error fetching cart count:", e));
    }
  }

  // Observe specific context elements for changes (e.g., modal visibility)
  function setupContextObserver(contextElement, contextConfig) {
    const observer = new MutationObserver((mutations) => {
      let shouldProcess = false;
      for (const mutation of mutations) {
        if (((contextConfig.triggerEvents.includes('class') && mutation.attributeName === 'class') || 
            (contextConfig.triggerEvents.includes('style') && mutation.attributeName === 'style'))) {
           const isPotentiallyVisible = contextElement.classList.contains('active') || 
                                      contextElement.classList.contains('open') || 
                                      contextElement.classList.contains('reveal') || 
                                      contextElement.style.display !== 'none' || 
                                      contextElement.offsetParent !== null;
           if (isPotentiallyVisible) { shouldProcess = true; break; }
        }
      }
      if (shouldProcess && contextElement.dataset.fitTypeInitialized !== 'true') {
        setTimeout(() => {
          log(`Trigger detected, processing context: ${contextConfig.name}`);
          processContext(contextElement, contextConfig);
          contextElement.dataset.fitTypeInitialized = 'true';
        }, 300);
      }
    });
    observer.observe(contextElement, { attributes: true, attributeFilter: ['class', 'style'] });
    log(`MutationObserver set up for context: ${contextConfig.name}`);
  }

  // Observe the whole document for dynamically added nodes
  function setupGlobalMutationObserver() {
    // Debounce function to prevent excessive processing
    const debounce = (func, wait) => {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          timeout = null;
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    };
    
    // Create a debounced version of modifyAddonButtonAttributes
    const debouncedModifyButtons = debounce(modifyAddonButtonAttributes, 250);
    
    const observer = new MutationObserver(mutations => {
      let hasRelevantChanges = false;
      
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType !== 1) return;
            
            // Check for add-on sections
            if ((node.matches && node.matches('.additional-add_ons')) || 
                (node.querySelectorAll && node.querySelectorAll('.additional-add_ons').length > 0)) {
                hasRelevantChanges = true;
            }
            
            // Check for context elements
            contextRegistry.forEach(config => {
                if ((node.matches && node.matches(config.selector)) || 
                    (node.querySelectorAll && node.querySelectorAll(config.selector).length > 0)) {
                    hasRelevantChanges = true;
                }
            });
            
            // Check for swatch containers
            if (node.querySelectorAll && node.querySelectorAll(SYSTEM_CONFIG.swatchContainerSelector).length > 0) {
                hasRelevantChanges = true;
            }
          });
        }
      });
      
      // Only process if relevant changes were detected
      if (hasRelevantChanges) {
        // Modify buttons with debounce
        debouncedModifyButtons();
        
        // Process context elements
        mutations.forEach(mutation => {
          if (mutation.type === 'childList' && mutation.addedNodes.length) {
            mutation.addedNodes.forEach(node => {
              if (node.nodeType !== 1) return;
              
              // Initialize contexts/swatches
              contextRegistry.forEach(config => {
                  if (node.matches && node.matches(config.selector)) { 
                      initializeContext(node, config); 
                  }
                  if (node.querySelectorAll) { 
                      node.querySelectorAll(config.selector).forEach(elem => initializeContext(elem, config)); 
                  }
              });
              
              // Check swatch containers
              if(node.querySelectorAll) {
                  node.querySelectorAll(SYSTEM_CONFIG.swatchContainerSelector).forEach(container => { 
                      if (!container.closest('.additional-add_ons')) { 
                          checkNewSwatchContainer(container); 
                      } 
                  });
              }
            });
          }
        });
      }
    });
    
    observer.observe(document.body, { childList: true, subtree: true, attributes: false });
    log("Global MutationObserver initialized.");
  }

  // Check newly added swatch containers
  function checkNewSwatchContainer(container) {
    if (initializedContainers.has(container)) return;
    for (const contextConfig of contextRegistry) {
      const contextElement = container.closest(contextConfig.selector);
      if (contextElement) {
         log(`New swatch container found within context: ${contextConfig.name}`, container);
         processContext(contextElement, contextConfig);
         return;
      }
    }
     const globalMetafields = document.getElementById(SYSTEM_CONFIG.productMetafieldsId);
     if(globalMetafields && !container.closest('.section-product-template, #quick-buy-modal')) {
          log("Initializing new container found outside known contexts, using global metafields.");
          processMetafields(globalMetafields, document.body);
     } else if (!globalMetafields) {
         warn("New swatch container found, but no context or global metafields element identified.", container);
     }
  }

  // Create Fit Type buttons UI
  function createFitTypeButtons(swatchContainer, sizeSection, productData) {
     if (swatchContainer.querySelector('.swatch-fit-type-container')) {
        log("Fit type buttons already exist, skipping creation.", swatchContainer); return;
     }
    const sizeSectionDiv = sizeSection.querySelector('.swatch-single');
    let presetClass = 'swatch-preset-default'; // Default class
    if (sizeSectionDiv && sizeSectionDiv.className) {
      const presetMatch = sizeSectionDiv.className.match(/swatch-preset-\d+/);
      if (presetMatch && presetMatch[0]) { presetClass = presetMatch[0]; }
      else { log("Could not extract preset class, using default."); }
    }
    const fitTypeContainer = document.createElement('div');
    const uniqueSuffix = swatchContainer.closest('[data-product-id]')?.dataset.productId || Date.now();
    fitTypeContainer.id = `swatch-fit-type-${uniqueSuffix}`;
    fitTypeContainer.setAttribute('option-name', SYSTEM_CONFIG.fitTypePropertyName);
    fitTypeContainer.className = 'swatch-fit-type-container';
    fitTypeContainer.style.marginTop = USER_CONFIG.styles.marginTop;
    fitTypeContainer.style.marginBottom = USER_CONFIG.styles.marginBottom;
    fitTypeContainer.innerHTML = `
      <div class="swatch-single ${presetClass} swatch-view-stack">
        <fieldset><legend><label class="swatch-label swatch-label-button"><span class="swatch-option-name">${SYSTEM_CONFIG.fitTypePropertyName}</span></label></legend>
          <ul class="swatch-view swatch-view-button fit-type-buttons" role="radiogroup" style="display: flex; flex-wrap: wrap; gap: ${USER_CONFIG.styles.buttonGap};"></ul>
        </fieldset>
      </div>
      <style>#${fitTypeContainer.id} .fit-type-buttons .swatch-button { padding: ${USER_CONFIG.styles.buttonPadding}; }</style>`;
    sizeSection.parentNode.insertBefore(fitTypeContainer, sizeSection); // Insert before size section
    const buttonContainer = fitTypeContainer.querySelector('.fit-type-buttons');
    const fitTypesWithAvailability = getFitTypesWithAvailability(productData.variants);
    fitTypesWithAvailability.forEach((fitTypeInfo, index) => {
      const { fitType, hasAvailableVariants } = fitTypeInfo;
      const isFirst = index === 0; const isLast = index === fitTypesWithAvailability.length - 1;
      const li = document.createElement('li');
      li.className = 'swatch-view-item';
      if (USER_CONFIG.showSoldOutFitTypes && !hasAvailableVariants) { li.classList.add('swatch-item-unavailable'); }
      li.setAttribute('orig-value', fitType);
      li.setAttribute('aria-label', fitType + (!hasAvailableVariants && USER_CONFIG.showSoldOutFitTypes ? ' - Sold Out' : ''));
      li.setAttribute('role', 'radio'); li.setAttribute('aria-checked', 'false'); li.tabIndex = 0;
      li.innerHTML = `<div class="${isFirst?'first-btn':''} ${isLast?'last-btn':''} swatch-button swatch-selector star-set-btn swatch-allow-animation" swatch-option="fit-type" type="swatch-button" data-value="${fitType}" orig-value="${fitType}"><div swatch-inside="true" class="swatch-title-and-icon-container"><div class="swatch-button-title-text" swatch-inside="true"><span swatch-inside="true">${fitType}</span></div></div></div>`;
      buttonContainer.appendChild(li);
      // Add event listeners for click and keyboard interaction
      li.addEventListener('click', () => { if (!li.classList.contains('swatch-item-unavailable')) { selectFitType(fitType, li, sizeSection, productData); } });
      li.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!li.classList.contains('swatch-item-unavailable')) { selectFitType(fitType, li, sizeSection, productData); } } });
    });
    // Auto-select first available fit type
    if (fitTypesWithAvailability.length > 0) {
      const firstAvailable = fitTypesWithAvailability.find(ft => ft.hasAvailableVariants);
      const defaultSelection = firstAvailable || fitTypesWithAvailability[0];
      const defaultButton = buttonContainer.querySelector(`.swatch-view-item[orig-value="${defaultSelection.fitType}"]`);
      if (defaultButton) { setTimeout(() => { if (defaultButton.getAttribute('aria-checked') !== 'true') { selectFitType(defaultSelection.fitType, defaultButton, sizeSection, productData); log(`Default fit type selected: ${defaultSelection.fitType}`); } }, 50); }
    }
  }

  // Get unique fit types and their availability
  function getFitTypesWithAvailability(variants) {
    const fitTypesMap = {};
    variants.forEach(variant => {
      if (variant.fitType) {
        if (!fitTypesMap[variant.fitType]) { fitTypesMap[variant.fitType] = { fitType: variant.fitType, hasAvailableVariants: false }; }
        if (variant.available) { fitTypesMap[variant.fitType].hasAvailableVariants = true; }
      }
    });
    return Object.values(fitTypesMap);
  }

  // Handle selecting a fit type button
  function selectFitType(fitType, selectedButton, sizeSection, productData) {
    const container = selectedButton.closest('.swatch-fit-type-container');
    if (!container) return;
    const buttons = container.querySelectorAll('.fit-type-buttons .swatch-view-item');
    // Update button states
    buttons.forEach(btn => {
      const isSelected = btn === selectedButton;
      btn.setAttribute('aria-checked', isSelected.toString());
      const swatchBtnDiv = btn.querySelector('.swatch-button');
      if (swatchBtnDiv) { swatchBtnDiv.classList.toggle('swatch-selected', isSelected); }
    });
    // Update sizes based on selected fit type
    const sizesForFitType = getSizesForFitType(productData.variants, fitType);
    updateSizeVisibility(sizesForFitType, sizeSection);
    selectFirstValidSize(sizesForFitType, sizeSection);
    // Add/update property in main form
    addFitTypeToCartForm(fitType, sizeSection);
    currentMainProductFitType = fitType;
    log(`Selected Fit Type: ${fitType}`);
  }

  // Add/Update the hidden input field in the MAIN form ONLY
  function addFitTypeToCartForm(fitType, sizeSection) {
    try {
        // Robust Form Finding Logic - cache when possible
        let form = null;
        const productArea = sizeSection.closest('.product-area[data-product-id][data-section-id]');
        
        if (productArea) {
            const productId = productArea.dataset.productId;
            const sectionId = productArea.dataset.sectionId;
            const mainFormId = `product-form-${sectionId}-${productId}`;
            const cacheKey = `mainForm-${productId}`;
            
            form = getFromCache(cacheKey, `#${mainFormId}`);
            if (form) { 
                log("Using cached main form with ID:", mainFormId); 
            } else {
                form = document.getElementById(mainFormId);
                if (form) { log("Targeting main form using specific ID:", mainFormId); }
            }
        }
        
        if (!form) {
            form = sizeSection.closest('form[action*="/cart/add"]');
            if (form && !form.closest('.additional-add_ons')) { 
                log("Targeting main form using closest form selector."); 
            } else if (form) { 
                form = null; // Ignore if inside add-ons
            }
        }
        
        if (!form) {
            const formCandidates = document.querySelectorAll('form[action*="/cart/add"].product-purchase-form');
            for(let candidate of formCandidates) { 
                if (!candidate.closest('.additional-add_ons')) { 
                    form = candidate; 
                    log("Targeting main form using broader search, excluding add-ons."); 
                    break; 
                } 
            }
        }
        
        if (!form) { 
            error("CRITICAL: Could not find the main product form to add Fit Type property."); 
            return; 
        }

        const propName = `properties[${SYSTEM_CONFIG.fitTypePropertyName}]`;
        let fitTypeInput = form.querySelector(`input[type="hidden"][name="${propName}"]`);
        
        if (!fitTypeInput) {
            fitTypeInput = document.createElement('input');
            fitTypeInput.type = 'hidden'; 
            fitTypeInput.name = propName;
            fitTypeInput.className = 'main-product-fit-type-property';
            form.appendChild(fitTypeInput);
            log(`Created line item property input for ${SYSTEM_CONFIG.fitTypePropertyName} in form:`, form.id || form);
        }
        
        fitTypeInput.value = fitType;
        log(`Set ${SYSTEM_CONFIG.fitTypePropertyName} to "${fitType}" in form:`, form.id || form);
    } catch (err) {
      error("Error adding fit type to cart form:", err);
    }
  }

  // Get available sizes for a specific fit type
  function getSizesForFitType(variants, fitType) {
    const sizesMap = {};
    variants.forEach(variant => {
      if (variant.fitType === fitType && variant.option1) {
        // Initialize or update availability
        if (!sizesMap[variant.option1] || !sizesMap[variant.option1].available) {
             sizesMap[variant.option1] = { value: variant.option1, available: variant.available };
         }
         // Ensure 'available' is true if *any* variant of this size/fit is available
         if(variant.available) {
             sizesMap[variant.option1].available = true;
         }
      }
    });
    return Object.values(sizesMap);
  }

  // Update visibility and availability status of size swatches
  function updateSizeVisibility(sizesForFitType, sizeSection) {
    const sizeItems = sizeSection.querySelectorAll('.swatch-view-item');
    const validSizeValues = new Set(sizesForFitType.map(size => size.value));
    sizeItems.forEach(item => {
      const sizeValue = item.getAttribute('orig-value');
      const isValidForFit = validSizeValues.has(sizeValue);
      item.style.display = isValidForFit ? '' : 'none'; // Show/hide
       if(isValidForFit){ // Update availability class and label only if visible
            const sizeInfo = sizesForFitType.find(s => s.value === sizeValue);
            if(sizeInfo){
                 item.classList.toggle('swatch-item-unavailable', !sizeInfo.available);
                 const currentLabel = item.getAttribute('aria-label')?.split(' - ')[0] || sizeValue;
                 item.setAttribute('aria-label', currentLabel + (!sizeInfo.available ? ' - Sold Out' : ''));
            }
       } else { item.classList.remove('swatch-item-unavailable'); } // Clean up class if hidden
    });
  }

  // Select the first valid/available size if the current selection becomes invalid
  function selectFirstValidSize(sizesForFitType, sizeSection) {
     const sizeItems = sizeSection.querySelectorAll('.swatch-view-item');
     const currentSelected = sizeSection.querySelector('.swatch-view-item[aria-checked="true"]');
     const currentSelectedValue = currentSelected ? currentSelected.getAttribute('orig-value') : null;
     let currentSelectionIsValid = (currentSelected && currentSelected.style.display !== 'none' && sizesForFitType.some(s => s.value === currentSelectedValue));
     
     if (!currentSelectionIsValid) {
         let newlySelected = false;
         
         // Prioritize available & visible
         for (const sizeInfo of sizesForFitType) {
             if (sizeInfo.available) {
                 const availableSizeItem = sizeSection.querySelector(`.swatch-view-item[orig-value="${sizeInfo.value}"]`);
                 if (availableSizeItem && availableSizeItem.style.display !== 'none') {
                     if (availableSizeItem.getAttribute('aria-checked') !== 'true') { 
                         availableSizeItem.click(); 
                     }
                     currentMainProductSize = sizeInfo.value;
                     newlySelected = true;
                     log(`Auto-selected first available size: ${sizeInfo.value}`);
                     break;
                 }
             }
         }
         
         // Fallback to first visible
         if (!newlySelected) {
             for (const sizeItem of sizeItems) {
                 if (sizeItem.style.display !== 'none') {
                      if (sizeItem.getAttribute('aria-checked') !== 'true') { 
                          sizeItem.click(); 
                      }
                      currentMainProductSize = sizeItem.getAttribute('orig-value');
                      newlySelected = true;
                      log(`Auto-selected first visible size (fallback): ${currentMainProductSize}`);
                      break;
                 }
             }
         }
         
        // Handle case where nothing is selectable
        if (!newlySelected) {
             currentMainProductSize = null;
             warn("No valid size options available or visible to auto-select for this fit type.");
             const hiddenVariantSelector = sizeSection.closest('form')?.querySelector('select[name="id"]');
             if(hiddenVariantSelector) {
                 hiddenVariantSelector.value = '';
                 hiddenVariantSelector.dispatchEvent(new Event('change', { bubbles: true }));
             }
        }
     } else {
         currentMainProductSize = currentSelectedValue;
         log(`Current size selection ${currentMainProductSize} remains valid.`);
     }
  }

  // --- Initialization ---
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize(); // DOM already loaded
  }

})();